/**
 * Expert / Provider Dashboard — dark navy layout matching Infirmly mockup.
 * Shows Welcome banner with avatar + verified badge, Profile Completion,
 * stat cards (Requests / Appointments / Messages), Recent Activity, Earnings.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BellRing, ShieldCheck, UserPlus, CalendarDays, MessageSquare, Briefcase,
  Mail, ChevronRight, TrendingUp, Wallet, MapPin, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO = "https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png";

const localized = (lang, dict) => dict[lang] || dict.en;

const ExpertDashboardHome = ({ user, language = "en" }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ new_requests: 0, upcoming_appointments: 0, unread_messages: 0, earnings_this_month: 0 });
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats/dashboard`, { withCredentials: true })
      .then(r => {
        setStats({
          new_requests: r.data.new_requests ?? r.data.active_jobs ?? 0,
          upcoming_appointments: r.data.upcoming_appointments ?? 0,
          unread_messages: r.data.unread_messages ?? 0,
          earnings_this_month: r.data.earnings_this_month ?? 0,
        });
        setProfileCompletion(r.data.profile_completion ?? r.data.completion ?? 0);
        setActivity(r.data.recent_activity || []);
      })
      .catch(() => {});
  }, []);

  const t = (key, dict) => localized(language, dict);

  return (
    <div
      className="rounded-3xl border border-white/10 p-5 sm:p-6 space-y-5 text-white"
      style={{ background: "linear-gradient(135deg, #0A1A3E 0%, #0F2557 100%)" }}
      data-testid="expert-dark-home"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <img src={LOGO} alt="Infirmly" className="h-9" />
        <button className="relative w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition" data-testid="expert-notif-btn">
          <BellRing className="w-4 h-4 text-blue-100" />
          {stats.unread_messages > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {stats.unread_messages > 9 ? "9+" : stats.unread_messages}
            </span>
          )}
        </button>
      </div>

      {/* Welcome row */}
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-blue-200/70 text-sm">{t("", { de: "Willkommen zurück,", en: "Welcome back,", fr: "Bon retour,", it: "Bentornato/a," })}</p>
          <h1 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
            {user?.name || "Expert"}
          </h1>
          <div className="flex items-center gap-1.5 mt-1.5">
            <ShieldCheck className="w-4 h-4 text-[#60a5fa]" />
            <span className="text-[#60a5fa] text-xs font-semibold">
              {t("", { de: "Verifizierter Experte", en: "Verified Expert", fr: "Expert vérifié", it: "Esperto verificato" })}
            </span>
          </div>
        </div>
        <div className="relative flex-shrink-0">
          <div className="w-16 h-16 rounded-full bg-white/5 border-2 border-[#60a5fa]/40 overflow-hidden flex items-center justify-center">
            {user?.profile_picture ? (
              <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-white">{user?.name?.charAt(0) || "E"}</span>
            )}
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-[#0044CC] to-[#1d4ed8] border-2 border-[#0A1A3E] flex items-center justify-center">
            <ShieldCheck className="w-3 h-3 text-white" />
          </div>
        </div>
      </div>

      {/* Profile Completion */}
      <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4" data-testid="profile-completion">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white font-semibold text-sm">
            {t("", { de: "Profil-Vollständigkeit", en: "Profile Completion", fr: "Profil complété", it: "Completamento profilo" })}
          </span>
          <span className="text-[#60a5fa] font-bold text-lg">{profileCompletion}%</span>
        </div>
        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#0044CC] to-[#60a5fa] transition-all duration-700"
            style={{ width: `${Math.min(100, Math.max(0, profileCompletion))}%` }}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center"><span className="w-1.5 h-1.5 rounded-full bg-green-400" /></span>
          <p className="text-blue-100/60 text-xs">
            {profileCompletion >= 100
              ? t("", { de: "Großartig! Ihr Profil ist vollständig.", en: "Great! Your profile is fully complete.", fr: "Excellent ! Votre profil est complet.", it: "Ottimo! Il tuo profilo è completo." })
              : t("", { de: "Vervollständigen Sie Ihr Profil für mehr Sichtbarkeit.", en: "Complete your profile for more visibility.", fr: "Complétez votre profil pour plus de visibilité.", it: "Completa il profilo per maggiore visibilità." })}
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatCard
          icon={UserPlus} value={stats.new_requests}
          label={t("", { de: "Neue Anfragen", en: "New Requests", fr: "Nouvelles demandes", it: "Nuove richieste" })}
          cta={t("", { de: "Alle ansehen", en: "View all", fr: "Voir tout", it: "Vedi tutto" })}
          onClick={() => navigate("/dashboard/jobs")}
          testid="stat-new-requests"
        />
        <StatCard
          icon={CalendarDays} value={stats.upcoming_appointments}
          label={t("", { de: "Termine", en: "Upcoming Appointments", fr: "Rendez-vous", it: "Appuntamenti" })}
          cta={t("", { de: "Kalender", en: "View calendar", fr: "Calendrier", it: "Calendario" })}
          onClick={() => navigate("/dashboard/appointments")}
          testid="stat-appointments"
        />
        <StatCard
          icon={MessageSquare} value={stats.unread_messages}
          label={t("", { de: "Nachrichten", en: "Messages", fr: "Messages", it: "Messaggi" })}
          cta={t("", { de: "Lesen", en: "View messages", fr: "Lire", it: "Leggi" })}
          onClick={() => navigate("/dashboard/messages")}
          showBadge
          testid="stat-messages"
        />
      </div>

      {/* Recent Activity */}
      <div data-testid="recent-activity">
        <h2 className="text-white font-bold text-base mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          {t("", { de: "Aktuelle Aktivität", en: "Recent Activity", fr: "Activité récente", it: "Attività recente" })}
        </h2>
        <div className="space-y-2">
          {activity.length === 0 ? (
            <ActivityItem
              icon={Briefcase}
              title={t("", { de: "Noch keine Aktivität", en: "No recent activity", fr: "Aucune activité récente", it: "Nessuna attività recente" })}
              subtitle=""
              time=""
            />
          ) : (
            activity.slice(0, 3).map((act, i) => (
              <ActivityItem
                key={i}
                icon={act.type === "appointment" ? CalendarDays : act.type === "message" ? MessageSquare : Briefcase}
                title={act.title}
                subtitle={act.subtitle}
                time={act.time}
                tag={act.tag}
                meta={act.meta}
              />
            ))
          )}
        </div>
      </div>

      {/* Earnings Overview */}
      <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 flex items-center gap-4" data-testid="earnings-overview">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm">
            {t("", { de: "Einnahmen", en: "Earnings Overview", fr: "Aperçu des gains", it: "Panoramica guadagni" })}
          </p>
          <p className="text-blue-200/60 text-xs">
            {t("", { de: "Diesen Monat", en: "This Month", fr: "Ce mois", it: "Questo mese" })}
          </p>
          <p className="text-white text-xl font-extrabold mt-0.5" style={{ fontFamily: "Chivo, sans-serif" }}>
            CHF {stats.earnings_this_month.toLocaleString("de-CH")}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <span className="text-emerald-300 text-xs font-bold">+18%</span>
          </div>
          <p className="text-blue-200/50 text-[10px] mt-1">
            {t("", { de: "vs. Vormonat", en: "vs last month", fr: "vs mois dernier", it: "vs mese scorso" })}
          </p>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, cta, onClick, showBadge, testid }) => (
  <button
    onClick={onClick}
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 text-left hover:bg-white/[0.08] hover:border-[#60a5fa]/30 transition-all"
    data-testid={testid}
  >
    <p className="text-blue-100/60 text-[11px] leading-tight font-medium">{label}</p>
    <div className="flex items-center gap-2 mt-2">
      <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-[#0044CC] to-[#1d4ed8] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-white" />
        {showBadge && value > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">
            {value > 9 ? "9+" : value}
          </span>
        )}
      </div>
      <span className="text-white text-2xl font-extrabold" style={{ fontFamily: "Chivo, sans-serif" }}>{value}</span>
    </div>
    <p className="text-[#60a5fa] text-[10px] mt-2 flex items-center gap-0.5 font-medium">{cta} <ChevronRight className="w-3 h-3" /></p>
  </button>
);

const ActivityItem = ({ icon: Icon, title, subtitle, time, tag, meta }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 flex gap-3 items-start">
    <div className="w-10 h-10 rounded-xl bg-[#0044CC]/15 border border-[#60a5fa]/20 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#60a5fa]" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <p className="text-white font-semibold text-sm truncate">{title}</p>
        {tag && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-[#60a5fa]/40 text-[#60a5fa]">{tag}</span>}
      </div>
      {subtitle && <p className="text-blue-100/60 text-xs truncate">{subtitle}</p>}
      {meta && (
        <p className="text-blue-100/50 text-xs flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" />{meta}
        </p>
      )}
    </div>
    {time && <span className="text-blue-100/50 text-xs flex items-center gap-1 flex-shrink-0"><Clock className="w-3 h-3" />{time}</span>}
  </div>
);

export default ExpertDashboardHome;
